import { useCallback } from "react";
import useCollator from "hooks/useCollator";
import StringHelpers from "helpers/StringHelpers";
import useMergeProps from "hooks/useMergeProps";

interface UseCollatorOptions extends Intl.CollatorOptions {}

export type NaturalSortComparator = (a: string, b: string) => number;

const DEFAULT_OPTIONS = { numeric: true };

/**
 * Locale-aware collator with human-friendly naturalCompare:
 * - Aware of all Unicode letters (ä, ö, é, あ, 한, etc.)
 * - Sorts letters → numbers → symbols
 */
function useNaturalSort(options?: UseCollatorOptions): NaturalSortComparator {
  const mergedOptions = useMergeProps(DEFAULT_OPTIONS, options);
  const collator = useCollator(mergedOptions);

  const naturalSort = useCallback(
    (a: string, b: string) => {
      const aStartsWithLetter = StringHelpers.isFirstCharLetter(a);
      const bStartsWithLetter = StringHelpers.isFirstCharLetter(b);
      const aStartsWithDigit = StringHelpers.isFirstCharDigit(a);
      const bStartsWithDigit = StringHelpers.isFirstCharDigit(b);

      // Both are letters or both are digits
      if ((aStartsWithLetter && bStartsWithLetter) || (aStartsWithDigit && bStartsWithDigit)) {
        return collator.compare(a, b);
      }

      // One is letter, the other is digit
      if (aStartsWithLetter && bStartsWithDigit) {
        return -1; // letters come before digits
      }
      if (aStartsWithDigit && bStartsWithLetter) {
        return 1; // digits come after letters
      }

      if (!aStartsWithLetter && !aStartsWithDigit && (bStartsWithLetter || bStartsWithDigit)) {
        return 1;
      }
      if (!bStartsWithLetter && !bStartsWithDigit && (aStartsWithLetter || aStartsWithDigit)) {
        return -1;
      }
      // Both are symbols
      return collator.compare(a, b);
    },
    [collator]
  );

  return naturalSort;
}

export default useNaturalSort;
